import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter/dist/esm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getI18nStrings } from '../../shared/data/i18n';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { isOwnDomainUrl } from '../../shared/utils/urlUtils';
import DrawioViewer from '../../shared/components/DrawioViewer';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import { Mermaid } from '../../shared/components/Mermaid';
import '../../shared/styles/markdown-content.css';
import './About.css';

type TabType = 'development' | 'architecture' | 'other';

/**
 * Aboutページコンポーネント
 */
function About() {
  const lang = useLanguage();
  const strings = getI18nStrings(lang);
  const [activeTab, setActiveTab] = useState<TabType>('other');
  const [diagramVersions, setDiagramVersions] = useState<Array<{ version: string; filename: string; description?: { ja: string; en: string } }>>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [overviewMarkdown, setOverviewMarkdown] = useState<string>('');
  const [developmentMarkdown, setDevelopmentMarkdown] = useState<string>('');
  const [loadingOverview, setLoadingOverview] = useState<boolean>(true);
  const [loadingDevelopment, setLoadingDevelopment] = useState<boolean>(true);

  // メタタグを設定
  const siteUrl = getSiteUrl();
  const aboutUrl = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}/about`;
  const description = lang === 'ja'
    ? 'このサイトについて、開発環境、システム構成図などの情報を公開しています。'
    : 'Information about this site, development environment, system architecture diagrams, etc.';
  
  useMetaTags({
    title: 'About | Version0.1',
    description,
    ogp: {
      title: 'About | Version0.1',
      description,
      url: aboutUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary' as const,
      title: 'About | Version0.1',
      description,
      image: '/og-image.png',
    },
    canonicalUrl: aboutUrl,
  });

  // 概要のmarkdownファイルを読み込む
  useEffect(() => {
    const fetchOverviewMarkdown = async () => {
      try {
        const filename = lang === 'ja' ? 'overview-ja.md' : 'overview-en.md';
        const response = await fetch(`/about/${filename}`);
        if (response.ok) {
          const text = await response.text();
          setOverviewMarkdown(text);
        } else {
          console.error('Failed to fetch overview markdown');
          setOverviewMarkdown('');
        }
      } catch (err) {
        console.error('Failed to fetch overview markdown:', err);
        setOverviewMarkdown('');
      } finally {
        setLoadingOverview(false);
      }
    };

    fetchOverviewMarkdown();
  }, [lang]);

  // 開発環境のmarkdownファイルを読み込む
  useEffect(() => {
    const fetchDevelopmentMarkdown = async () => {
      try {
        const filename = lang === 'ja' ? 'development-ja.md' : 'development-en.md';
        const response = await fetch(`/about/${filename}`);
        if (response.ok) {
          const text = await response.text();
          setDevelopmentMarkdown(text);
        } else {
          console.error('Failed to fetch development markdown');
          setDevelopmentMarkdown('');
        }
      } catch (err) {
        console.error('Failed to fetch development markdown:', err);
        setDevelopmentMarkdown('');
      } finally {
        setLoadingDevelopment(false);
      }
    };

    fetchDevelopmentMarkdown();
  }, [lang]);

  // 言語に応じたファイル名を取得する関数
  const getLocalizedFilename = (baseFilename: string, currentLang: string): string => {
    // 拡張子を取得
    const lastDotIndex = baseFilename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // 拡張子がない場合はそのまま返す
      return baseFilename;
    }
    
    const nameWithoutExt = baseFilename.substring(0, lastDotIndex);
    const ext = baseFilename.substring(lastDotIndex);
    const langSuffix = currentLang === 'ja' ? '-ja' : '-en';
    
    // 言語サフィックス付きのファイル名を返す
    // 例: "v1.0-system-architecture.svg" → "v1.0-system-architecture-ja.svg"
    return `${nameWithoutExt}${langSuffix}${ext}`;
  };

  // システム構成図のバージョン一覧を取得
  useEffect(() => {
    const fetchDiagramVersions = async () => {
      try {
        // diagrams-manifest.jsonからファイルリストを取得
        const response = await fetch('/diagrams/diagrams-manifest.json');
        if (!response.ok) {
          // manifest.jsonが存在しない場合は空のリストを返す
          setDiagramVersions([]);
          return;
        }
        
        const manifest = await response.json();
        const versions: Array<{ version: string; filename: string; description?: { ja: string; en: string } }> = manifest.diagrams || [];
        
        // バージョン番号を比較する関数（セマンティックバージョニング対応）
        const compareVersions = (a: string, b: string): number => {
          const aParts = a.split('.').map(Number);
          const bParts = b.split('.').map(Number);
          const maxLength = Math.max(aParts.length, bParts.length);
          
          for (let i = 0; i < maxLength; i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;
            if (aPart > bPart) return -1; // 新しい順（降順）
            if (aPart < bPart) return 1;
          }
          return 0;
        };
        
        // バージョンでソート（新しい順）
        const sortedVersions = versions.sort((a, b) => compareVersions(a.version, b.version));
        setDiagramVersions(sortedVersions);
        
        // 最新版を選択
        if (sortedVersions.length > 0) {
          setSelectedVersion(sortedVersions[0].filename);
        }
      } catch (err) {
        console.error('Failed to fetch diagram versions:', err);
        setDiagramVersions([]);
      }
    };

    if (activeTab === 'architecture') {
      fetchDiagramVersions();
    }
  }, [activeTab]);

  return (
    <div className="about-page">
      {/* タブUI */}
      <div className="about-page__tabs">
          <button
            type="button"
            className={`about-page__tab ${activeTab === 'other' ? 'active' : ''}`}
            onClick={() => setActiveTab('other')}
          >
            {strings.about.other.title}
          </button>
          <button
            type="button"
            className={`about-page__tab ${activeTab === 'development' ? 'active' : ''}`}
            onClick={() => setActiveTab('development')}
          >
            {strings.about.development.title}
          </button>
          <button
            type="button"
            className={`about-page__tab ${activeTab === 'architecture' ? 'active' : ''}`}
            onClick={() => setActiveTab('architecture')}
          >
            {strings.about.architecture.title}
          </button>
        </div>

      <div className="about-page__container">
        {/* タブコンテンツ */}
        <div className="about-page__content">
          {activeTab === 'development' && (
            <section className="about-page__section">
              {loadingDevelopment ? (
                <LoadingSpinner text="読み込み中..." />
              ) : (
                <div className="about-page__markdown markdown-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ href, children, ...props }: any) => {
                        const isOwnDomain = isOwnDomainUrl(href);
                        return (
                          <a 
                            href={href} 
                            {...props} 
                            {...(isOwnDomain ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                          >
                            {children}
                          </a>
                        );
                      },
                      code({ className, children, ...props }: any) {
                        // language-xxx または language-xxx:filename の形式を解析（ファイル名は無視）
                        const match = /language-([^:]+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        const codeString = String(children).replace(/\n$/, '');
                        const isInline = !className || !className.includes('language-');

                        // インラインコードの場合は通常のcodeタグを返す
                        if (isInline) {
                          return (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        }

                        // Mermaidの場合はMermaidコンポーネントを使用
                        if (language === 'mermaid') {
                          return <Mermaid chart={codeString} />;
                        }

                        // その他のコードブロックはシンタックスハイライトを適用
                        return (
                          <SyntaxHighlighter
                            language={language || 'text'}
                            style={vscDarkPlus}
                            PreTag="div"
                            {...props}
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        );
                      },
                    }}
                  >
                    {developmentMarkdown}
                  </ReactMarkdown>
                </div>
              )}
            </section>
          )}

          {activeTab === 'architecture' && (
            <section className="about-page__section">
              <h2 className="about-page__section-title">{strings.about.architecture.title}</h2>
              
              <div className="about-page__diagram-controls">
                <label htmlFor="diagram-version-select" className="about-page__version-label">
                  {strings.about.architecture.versionSelect}:
                </label>
                <select
                  id="diagram-version-select"
                  className="about-page__version-select"
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                >
                  {diagramVersions.length === 0 ? (
                    <option value="">{strings.about.architecture.noDiagram}</option>
                  ) : (
                    diagramVersions.map((version) => {
                      const description = version.description 
                        ? (lang === 'ja' ? version.description.ja : version.description.en)
                        : '';
                      return (
                        <option key={version.filename} value={version.filename}>
                          v{version.version}{description ? ` - ${description}` : ''}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              <div className="about-page__diagram-container">
                {selectedVersion ? (
                  <DrawioViewer filename={getLocalizedFilename(selectedVersion, lang)} />
                ) : (
                  <p className="about-page__no-diagram">{strings.about.architecture.noDiagram}</p>
                )}
              </div>
            </section>
          )}

          {activeTab === 'other' && (
            <section className="about-page__section">
              {loadingOverview ? (
                <LoadingSpinner text="読み込み中..." />
              ) : (
                <div className="about-page__markdown markdown-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ href, children, ...props }: any) => {
                        const isOwnDomain = isOwnDomainUrl(href);
                        return (
                          <a 
                            href={href} 
                            {...props} 
                            {...(isOwnDomain ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                          >
                            {children}
                          </a>
                        );
                      },
                      code({ className, children, ...props }: any) {
                        // language-xxx または language-xxx:filename の形式を解析（ファイル名は無視）
                        const match = /language-([^:]+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        const codeString = String(children).replace(/\n$/, '');
                        const isInline = !className || !className.includes('language-');

                        // インラインコードの場合は通常のcodeタグを返す
                        if (isInline) {
                          return (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        }

                        // Mermaidの場合はMermaidコンポーネントを使用
                        if (language === 'mermaid') {
                          return <Mermaid chart={codeString} />;
                        }

                        // その他のコードブロックはシンタックスハイライトを適用
                        return (
                          <SyntaxHighlighter
                            language={language || 'text'}
                            style={vscDarkPlus}
                            PreTag="div"
                            {...props}
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        );
                      },
                    }}
                  >
                    {overviewMarkdown}
                  </ReactMarkdown>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default About;
